import './index.scss';
import styles from './index.module.scss';
import { defineComponent, h, inject, PropType, reactive, VNode } from 'vue';
import { ClickableElement } from '@phi/touch';
import { TooltipPlacement, utility } from '@phi/components';
import { long_press_sensitivity } from '@phi/constants';
import { return_focus_to_scene } from '@phi/util/dom';
import type { IFrontendServiceContainer } from '@phi/frontend/service-container';
import { Option } from '@vaaas/fpts/option';
import { HelpTooltip } from '@phi/help';
import { pro_indicator } from '@components/icons';

/** A button to be used in toolbars. May or may not have a label.
 *
 * ---
 *
 * **Slots**
 *
 * | Slot | Description |
 * | --- | --- |
 * | `icon` | the icon to be used for this button |
 * | `label` | the optional label text to be used for this button |
 * | `tooltip` | the optional text that will appear in the tooltip |
 *
 * ---
 *
 * **Props**
 *
 * | Prop | Type | Description |
 * | --- | --- | --- |
 * | `disabled`| `boolean` | indicates that this button is not clickable |
 * | `active`| `boolean` | indicates that this button is in an active state, for example a mode that is turned on |
 * | `id`| `string` | HTML id used for the button |
 * | `wrapperId`| `string | undefined` | optional HTML id used for the button's wrapper |
 * | `tippy`| `'top' \| 'left' \| 'right' \| 'bottom'` | direction this button's tooltip will appear from |
 * | `long`| `boolean` | whether long press should be enabled for this button. enables the firing of the `longClick` event |
 * | `pro_indicator` | `boolean` | whether a floating icon will be shown over the regular icon indicating that this feature needs a Pro subscription
 *
 * ---
 *
 * **Events**
 *
 * | Event | Description |
 * | --- | --- |
 * | `click` | emitted when this button is clicked |
 * | `longClick` | emitted when this button is long pressed, if long pressing is enabled |
 */
const component = defineComponent({
    props: {
        /** indicates that this button is not clickable */
        disabled: {
            required: true,
            type: Boolean,
        },

        /** indicates that this button is in an active state, for example a mode that is turned on */
        active: {
            required: false,
            type: Boolean,
            default: false,
        },

        /** id used for the button */
        id: {
            required: true,
            type: String,
        },

        /** id used for the wrapper, if enabled */
        wrapperId: {
            required: false,
            type: String,
        },

        /** tippy placement on hover */
        tippy: {
            required: false,
            type: String as PropType<TooltipPlacement>,
        },

        /** whether the button should be highlighted for the current tutorial */
        selected: {
            required: false,
            type: Boolean,
            default: false,
        },

        /** whether long press should be enabled for this button. enables the firing of the `longClick` event */
        long: {
            required: false,
            type: Boolean,
            default: false,
        },

        help: {
            required: false,
            type: String,
        },

        pro_indicator: {
            required: false,
            type: Boolean,
            default: false,
        },
    },

    emits: {
        click: (x: MouseEvent) => true,
        longClick: (x: PointerEvent) => true,
    },

    setup(props, { emit, slots }) {
        const service_container: Option<IFrontendServiceContainer> = inject('service-container');

        const data = reactive({
            hover: false,
            clicked: false,
        });

        function after_click() {
            data.clicked = false;
            return_focus_to_scene();
        }

        function handle_click(x: PointerEvent) {
            data.clicked = true;
            emit('click', x);
            setTimeout(after_click, 250);
        }

        function handle_long_click(x: PointerEvent) {
            data.clicked = true;
            emit('longClick', x);
            setTimeout(after_click, 250);
        }

        function on_hover(x: PointerEvent) {
            data.hover = true;
        }

        function on_leave(x: PointerEvent) {
            data.hover = false;
        }

        return () => {
            const img = slots.icon && slots.icon();
            const label = slots.label && slots.label();

            const btn = (() => {
                if (label) return h(ClickableElement, {
                    long: props.long,
                    as: 'div',
                    class: {
                        'btn-wrapper': true,
                        disabled: props.disabled,
                    },
                    id: props.wrapperId,
                    onClick: handle_click,
                    onLongClick: handle_long_click,
                    'data-tippy-placement': props.tippy,
                    onEnter: on_hover,
                    onLeave: on_leave,
                    long_press_sensitivity: long_press_sensitivity * 2,
                }, () => [
                    h('button', {
                        type: 'button',
                        id: props.id,
                        'data-tippy-placement': props.tippy,
                        class: {
                            btn: true,
                            uibtn: true,
                            active: props.active && !props.disabled,
                            disabled: props.disabled,
                            'selected-tutorial': props.selected,
                            hover: data.hover,
                            clicked: data.clicked,
                        },
                    }, [
                        img,
                        FloatingProIndicator(props.pro_indicator),
                    ]),

                    h('div', { class: 'btn-label' }, label)
                ]);
                else return h(ClickableElement, {
                    long: props.long,
                    as: 'button',
                    type: 'button',
                    id: props.id,
                    'data-tippy-placement': props.tippy,
                    onClick: handle_click,
                    onLongClick: handle_long_click,
                    onEnter: on_hover,
                    onLeave: on_leave,
                    long_press_sensitivity: long_press_sensitivity * 2,
                    class: {
                        btn: true,
                        uibtn: true,
                        active: props.active && !props.disabled,
                        disabled: props.disabled,
                        pop: true,
                        'selected-tutorial': props.selected,
                        hover: data.hover,
                        clicked: data.clicked,
                    },
                }, () => [
                    img,
                    FloatingProIndicator(props.pro_indicator),
                ]);
            });

            if (service_container?.settings.tooltips)
                return h(HelpTooltip, {
                    placement: props.tippy,
                    help: props.help,
                }, {
                    default: btn,
                    tooltip: slots.tooltip,
                });
            else
                return btn();
        };
    },
});

function FloatingProIndicator(visible: boolean): VNode {
    return h('div', {
        class: {
            [styles.pro_indicator]: true,
            [utility.hidden]: !visible,
            [utility.absolute]: true,
        }
    }, pro_indicator());
}

export default component;
